import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useAnonymizeLead } from "@/hooks/use-leads";
import { useLeadDetail, type TimelineItem } from "@/hooks/use-lead-pools";
import { useLeadRules } from "@/hooks/use-lead-queue";
import { formatDateTimeShort, formatMNOK } from "@/lib/format";
import { eventDetail, eventLabel } from "@/lib/event-registry";
import { adminWhyHere } from "@/lib/lead-pool";
import { LEAD_OUTCOME_LABELS, LEAD_STATUS_LABELS } from "@/lib/lead-stats-labels";
import LeadAdminActions from "./LeadAdminActions";

interface Props {
  leadId: string;
  onBackToPool: (listId: string) => void;
  onBackToPools: () => void;
}

function timelineText(t: TimelineItem): string {
  if (t.kind === "attempt") {
    return [`${t.user_name ?? "Ukjent"} ringte · ${LEAD_OUTCOME_LABELS[t.outcome ?? ""] ?? t.outcome}`, t.note].filter(Boolean).join(". ");
  }
  return [eventLabel(t.event_type ?? ""), eventDetail(t.event_type ?? "", t.payload), t.user_name].filter(Boolean).join(" · ");
}

/** Leadkort for admin (VIB-3050 skjerm 6): hvorfor her, tidslinje, kontakter, plassering og handlinger. */
export default function AdminLeadCard({ leadId, onBackToPool, onBackToPools }: Props) {
  const { data: d, isLoading, error } = useLeadDetail(leadId);
  const { data: rules } = useLeadRules();
  const anonymize = useAnonymizeLead();

  if (isLoading) return <Skeleton className="h-96 w-full" />;
  if (error) return <p className="border border-border p-6 text-sm">{(error as Error).message}</p>;
  if (!d) return <p className="border border-border p-6 text-sm">Fant ikke leadet.</p>;
  const { lead } = d;
  const status = { ...lead, state: d.state, holder_name: d.holder_name };

  return (
    <article aria-label={`Leadkort: ${lead.name}`} className="space-y-5">
      <nav className="text-sm">
        <button type="button" className="underline" onClick={onBackToPools}>Pooler</button>
        <span className="mx-2 text-muted-foreground">/</span>
        <button type="button" className="underline" onClick={() => onBackToPool(d.list_id)}>{d.list_name}</button>
        <span className="mx-2 text-muted-foreground">/</span>
        <span>{lead.name}</span>
      </nav>
      <header className="space-y-1">
        <h2 className="font-display text-2xl font-semibold">{lead.name}</h2>
        <p className="text-sm text-muted-foreground">
          {[lead.organization_number, lead.industry_description, lead.municipality,
            lead.employees != null ? `${lead.employees} ansatte` : null,
            lead.revenue_nok != null ? `Omsetning ${formatMNOK(lead.revenue_nok)}` : null].filter(Boolean).join(" · ")}
        </p>
      </header>

      <div className="flex flex-wrap items-center justify-between gap-2">
        <LeadAdminActions leadIds={[lead.id]} currentListId={d.list_id} />
        <Button
          variant="ghost"
          size="sm"
          disabled={anonymize.isPending}
          onClick={() => {
            if (!confirm(`Slette ${lead.name}? Selskapet og kontaktpersonene anonymiseres og kan ikke hentes tilbake.`)) return;
            anonymize.mutate(lead.id, { onSuccess: () => onBackToPool(d.list_id) });
          }}
        >
          Slett person
        </Button>
      </div>

      {rules && (
        <section aria-label="Hvorfor her" className="border-l-2 border-foreground pl-3 text-sm">
          {adminWhyHere({ ...status, second_try: d.second_try, pool_name: d.list_name }, rules)}
        </section>
      )}

      <div className="grid gap-5 lg:grid-cols-[2fr_1fr]">
        <section aria-label="Tidslinje" className="space-y-2">
          <h3 className="font-display text-lg font-semibold">Tidslinje</h3>
          {d.timeline.length === 0 ? (
            <p className="text-sm text-muted-foreground">Ingen hendelser ennå.</p>
          ) : (
            <ol className="space-y-2 text-sm">
              {d.timeline.map((t, i) => (
                <li key={`${t.at}-${i}`} className="flex gap-3">
                  <span className="w-32 shrink-0 text-muted-foreground">{formatDateTimeShort(t.at)}</span>
                  <span>
                    {timelineText(t)}
                    {t.source === "system" && <span className="text-muted-foreground"> (systemet)</span>}
                  </span>
                </li>
              ))}
            </ol>
          )}
        </section>

        <div className="space-y-5">
          <section aria-label="Plassering" className="space-y-1 border-[1.5px] border-foreground p-3 text-sm">
            <h3 className="font-medium">Plassering</h3>
            <div>Pool: {d.list_name}</div>
            <div>Hos: {d.holder_name ?? "ingen"}</div>
            <div>Status: {LEAD_STATUS_LABELS[lead.status] ?? lead.status}</div>
            <div>Forsøk: {lead.attempts_count}{rules ? ` av ${rules.max_attempts}` : ""}</div>
            {lead.priority && <div>Prioritet: {lead.priority}</div>}
          </section>
          <section aria-label="Kontakter" className="space-y-1 text-sm">
            <h3 className="font-medium">Kontakter</h3>
            {d.contacts.length === 0 ? (
              <p className="text-muted-foreground">Ingen kontaktperson registrert.</p>
            ) : (
              <ul className="space-y-1">
                {d.contacts.map((c) => (
                  <li key={c.id}>
                    <span className="font-medium">{c.full_name}</span>
                    <span className="text-muted-foreground"> {[c.title, c.phone, c.email].filter(Boolean).join(" · ")}</span>
                  </li>
                ))}
              </ul>
            )}
          </section>
          {lead.pitch && (
            <section aria-label="Hvorfor relevant" className="space-y-1 text-sm">
              <h3 className="font-medium">Hvorfor relevant</h3>
              <p>{lead.pitch}</p>
            </section>
          )}
        </div>
      </div>
    </article>
  );
}
